if (!customElements.get('wlsn-heroblock-options')) {
  class WLSNHeroblockOptions extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        selected: 'is-selected',
        option: 'js-wlsn-heroblock-option',
        cta: 'js-wlsn-product-cta',
        oos: 'wollson-shop__button--oos',
        faqs: 'js-wlsn-heroblock-faqs',
        stickyOption: 'js-wlsn-heroblock-sticky-option',
        galleryImageSource: 'js-wlsn-heroblock-gallery-image-source',
        galleryImageImg: 'js-wlsn-heroblock-gallery-image-img',
        gallerySecondaryImageSource: 'js-wlsn-heroblock-gallery-secondary-image-source',
        gallerySecondaryImageImg: 'js-wlsn-heroblock-gallery-secondary-image-img',
        galleryBadgeImage: 'js-wlsn-heroblock-gallery-badge-image',
        gallerySwap: 'js-wlsn-heroblock-gallery-swap',
        gallerySwapTitle: 'js-wlsn-heroblock-gallery-swap-title',
        secondaryLink: 'js-wlsn-heroblock-secondary-link'
      };

      this.targets = {
        buyboxTitle: 'js-wlsn-heroblock-title',
        lineItemTitle: 'js-wlsn-heroblock-bundle-title',
        lineItemPrice: 'js-wlsn-heroblock-bundle-price',
        totalPrice: 'js-wlsn-heroblock-total',
        summaryPrice: 'js-wlsn-heroblock-summary-price',
        summaryComparePrice: 'js-wlsn-heroblock-summary-compare-price',
        summaryFootnote: 'js-wlsn-heroblock-summary-footnote',
        bundleHeaderLeft: 'js-wlsn-heroblock-bundle-header-left',
        bundleHeaderRight: 'js-wlsn-heroblock-bundle-header-right',
        description: 'js-wlsn-heroblock-description',
        galleryOverline: 'js-wlsn-heroblock-gallery-overline',
        galleryTitle: 'js-wlsn-heroblock-gallery-title'
      };

      this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    }

    connectedCallback() {
      this.scope = this.closest('.wlsn-atom-x-heroblock, .wollson-shop') || document;
      this.sectionScope = this.closest('.shopify-section') || document;
      this.options = Array.from(this.querySelectorAll(`.${this.classes.option}`));

      if (!this.options.length) return;

      this.captureDefaults();

      this.options.forEach((option) => {
        option.addEventListener('click', () => {
          this.selectOption(Number(option.dataset.index));
        });
      });

      this.handleMobileChange = () => this.applyTitle();

      if (this.mobileMediaQuery.addEventListener) {
        this.mobileMediaQuery.addEventListener('change', this.handleMobileChange);
      } else {
        this.mobileMediaQuery.addListener(this.handleMobileChange);
      }

      const preselected = this.options.find((option) =>
        option.classList.contains(this.classes.selected)
      );

      this.selectOption(preselected ? Number(preselected.dataset.index) : 0);
    }

    disconnectedCallback() {
      if (!this.handleMobileChange) return;

      if (this.mobileMediaQuery.removeEventListener) {
        this.mobileMediaQuery.removeEventListener('change', this.handleMobileChange);
      } else {
        this.mobileMediaQuery.removeListener(this.handleMobileChange);
      }
    }

    captureDefaults() {
      this.defaults = {
        text: new Map(),
        imgSrc: new Map(),
        href: new Map(),
        imgSrcset: new Map(),
        sourceSrcset: new Map()
      };

      Object.values(this.targets).forEach((className) => {
        this.scope.querySelectorAll(`.${className}`).forEach((element) => {
          if (!this.defaults.text.has(element)) {
            this.defaults.text.set(element, element.innerHTML);
          }
        });
      });

      this.scope.querySelectorAll(`.${this.classes.gallerySwapTitle}`).forEach((title) => {
        this.defaults.text.set(title, title.innerHTML);
      });

      this.scope.querySelectorAll(`.${this.classes.secondaryLink}`).forEach((link) => {
        this.defaults.text.set(link, link.innerHTML);
        this.defaults.href.set(link, link.getAttribute('href') || '');
      });

      this.scope
        .querySelectorAll(
          `.${this.classes.galleryImageImg}, .${this.classes.gallerySecondaryImageImg}, .${this.classes.gallerySwap} img`
        )
        .forEach((img) => {
          this.defaults.imgSrc.set(img, img.getAttribute('src') || '');
          this.defaults.imgSrcset.set(img, img.getAttribute('srcset') || '');
        });

      this.scope
        .querySelectorAll(
          `.${this.classes.galleryImageSource}, .${this.classes.gallerySecondaryImageSource}, .${this.classes.gallerySwap} source`
        )
        .forEach((source) => {
          this.defaults.sourceSrcset.set(source, source.getAttribute('srcset') || '');
        });
    }

    getData(index) {
      const script = this.querySelector(`script[data-wlsn-heroblock-option-data="${index}"]`);

      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('Error parsing heroblock option data', error);
        return null;
      }
    }

    selectOption(index) {
      const data = this.getData(index);

      this.options.forEach((option) => {
        const isSelected = Number(option.dataset.index) === index;

        option.classList.toggle(this.classes.selected, isSelected);
        option.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });

      this.scope.querySelectorAll(`.${this.classes.faqs}`).forEach((group) => {
        group.hidden = Number(group.dataset.index) !== index;
      });

      this.sectionScope.querySelectorAll(`.${this.classes.stickyOption}`).forEach((group) => {
        group.hidden = Number(group.dataset.index) !== index;
      });

      document.dispatchEvent(
        new CustomEvent('wlsn:heroblock-option-change', {
          detail: { index }
        })
      );

      if (!data) return;

      this.currentData = data;

      Object.keys(this.targets).forEach((key) => {
        if (key === 'buyboxTitle') return;

        const value = data[key];

        this.scope.querySelectorAll(`.${this.targets[key]}`).forEach((element) => {
          element.innerHTML = value || this.defaults.text.get(element) || '';
        });
      });

      this.applyTitle();

      if (data.checkoutLink) {
        this.scope.querySelectorAll(`.${this.classes.cta}`).forEach((cta) => {
          cta.setAttribute('href', data.checkoutLink);
        });
      }

      this.updateOutOfStock(data);

      if (data.btnPrice) {
        this.scope.querySelectorAll(`.${this.classes.cta} .js-wlsn-product-price`).forEach((price) => {
          price.innerHTML = data.btnPrice;
        });
      }

      if (data.btnLabel) {
        this.scope.querySelectorAll(`.${this.classes.cta}`).forEach((cta) => {
          const label = cta.lastChild;

          if (label && label.nodeType === Node.TEXT_NODE) {
            label.textContent = ` ${data.btnLabel}`;
          }
        });
      }

      this.updateSecondaryLink(data);
      this.updateGallery(data);
    }

    // Sticky CTAs are rendered per option with their own OOS state, so only in-section CTAs are toggled here
    updateOutOfStock(data) {
      // Sections that don't send an OOS flag keep their CTA state untouched
      if (typeof data.oos !== 'boolean') return;

      const isOos = data.oos;

      this.scope.querySelectorAll(`.${this.classes.cta}`).forEach((cta) => {
        cta.classList.toggle(this.classes.oos, isOos);

        if (isOos) {
          cta.setAttribute('aria-disabled', 'true');
          cta.setAttribute('tabindex', '-1');
        } else {
          cta.removeAttribute('aria-disabled');
          cta.removeAttribute('tabindex');
        }
      });
    }

    updateSecondaryLink(data) {
      this.scope.querySelectorAll(`.${this.classes.secondaryLink}`).forEach((link) => {
        const label = data.secondaryLinkLabel || this.defaults.text.get(link) || '';
        const href = data.secondaryLinkUrl || this.defaults.href.get(link) || '';

        link.innerHTML = label;
        link.setAttribute('href', href);
        link.hidden = !label || !href || data.oos === true;
      });
    }

    applyTitle() {
      const data = this.currentData;

      if (!data) return;

      const isMobile = this.mobileMediaQuery.matches;
      const value = (isMobile && data.buyboxTitleMobile) || data.buyboxTitle;

      this.scope.querySelectorAll(`.${this.targets.buyboxTitle}`).forEach((element) => {
        element.innerHTML = value || this.defaults.text.get(element) || '';
      });
    }

    updateGallery(data) {
      this.swapImages(this.classes.galleryImageImg, this.classes.galleryImageSource, data.galleryImageD, data.galleryImageM);
      this.swapImages(
        this.classes.gallerySecondaryImageImg,
        this.classes.gallerySecondaryImageSource,
        data.gallerySecondaryImageD,
        data.gallerySecondaryImageM
      );
      this.swapOptionImages(data);

      this.scope.querySelectorAll(`.${this.classes.galleryBadgeImage}`).forEach((badge) => {
        if (data.galleryBadgeImage) {
          badge.src = data.galleryBadgeImage;
          badge.hidden = false;
        } else {
          badge.hidden = true;
          badge.removeAttribute('src');
        }
      });
    }

    // Slides with their own swap image/title show it only while the matching product option is selected
    swapOptionImages(data) {
      this.scope.querySelectorAll(`.${this.classes.gallerySwap}`).forEach((swap) => {
        const isMatch = Boolean(data.productHandle) && swap.dataset.swapProduct === data.productHandle;

        swap.querySelectorAll('img').forEach((img) => {
          const src = isMatch ? swap.dataset.swapSrc : this.defaults.imgSrc.get(img);
          const srcset = isMatch ? '' : this.defaults.imgSrcset.get(img);

          if (src) {
            img.src = src;
          }

          if (srcset) {
            img.setAttribute('srcset', srcset);
          } else {
            img.removeAttribute('srcset');
          }
        });

        swap.querySelectorAll('source').forEach((source) => {
          const srcset = isMatch ? swap.dataset.swapSrcset : this.defaults.sourceSrcset.get(source);

          if (srcset) {
            source.setAttribute('srcset', srcset);
          }
        });
      });

      this.scope.querySelectorAll(`.${this.classes.gallerySwapTitle}`).forEach((title) => {
        const isMatch = Boolean(data.productHandle) && title.dataset.swapProduct === data.productHandle;

        title.innerHTML = isMatch ? title.dataset.swapTitle : this.defaults.text.get(title) || '';
      });
    }

    swapImages(imgClass, sourceClass, imageD, imageM) {
      this.scope.querySelectorAll(`.${imgClass}`).forEach((img) => {
        const src = imageD || this.defaults.imgSrc.get(img);
        const srcset = imageD ? '' : this.defaults.imgSrcset.get(img);

        if (src) {
          img.src = src;
        } else {
          img.removeAttribute('src');
        }

        if (srcset) {
          img.setAttribute('srcset', srcset);
        } else {
          img.removeAttribute('srcset');
        }
      });

      this.scope.querySelectorAll(`.${sourceClass}`).forEach((source) => {
        const srcset = imageM || this.defaults.sourceSrcset.get(source);

        if (srcset) {
          source.setAttribute('srcset', srcset);
        } else {
          source.removeAttribute('srcset');
        }
      });
    }
  }

  customElements.define('wlsn-heroblock-options', WLSNHeroblockOptions);
}
